import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { AttachmentField } from '../components/Attachment.jsx';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Category { slug: string; name: string; }
interface Income {
  id: string;
  categorySlug: string;
  source: string | null;
  amount: number;
  receivedOn: string;
  description: string | null;
  attachmentId: string | null;
}

export function IncomePage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const now = new Date();
  const firstOfMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [categorySlug, setCategorySlug] = useState('');

  const categoriesQ = useQuery({
    queryKey: ['income-categories'],
    queryFn: () => api<{ categories: Category[] }>('/api/v1/incomes/categories'),
  });
  const incomesQ = useQuery({
    queryKey: ['incomes', from, to, categorySlug],
    queryFn: () =>
      api<{ incomes: Income[]; totalPaise: number }>(
        `/api/v1/incomes?from=${from}&to=${to}${categorySlug ? `&categorySlug=${categorySlug}` : ''}`,
      ),
  });

  const catBySlug = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of categoriesQ.data?.categories ?? []) m.set(c.slug, c);
    return m;
  }, [categoriesQ.data]);

  return (
    <Page
      title="Income"
      actions={<Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New income'}</Button>}
    >
      <Card className="mb-4">
        <div className="grid gap-3 md:grid-cols-4 md:items-end">
          <Field label="From">
            <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Category">
            <select
              value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)}
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
            >
              <option value="">All</option>
              {(categoriesQ.data?.categories ?? []).map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </Field>
          <div className="text-right">
            <div className="text-xs text-slate-500">Total</div>
            <div className="text-lg font-medium">
              {Money.fromPaise(incomesQ.data?.totalPaise ?? 0).format()}
            </div>
            <div className="mt-1 text-[10px] text-slate-400">
              Excludes rent — see Rent page.
            </div>
          </div>
        </div>
      </Card>

      {showForm && (
        <div className="mb-4">
          <IncomeForm
            categories={categoriesQ.data?.categories ?? []}
            onSaved={() => {
              setShowForm(false);
              void qc.invalidateQueries({ queryKey: ['incomes'] });
            }}
          />
        </div>
      )}

      {incomesQ.isLoading ? (
        <Empty>Loading…</Empty>
      ) : !incomesQ.data?.incomes.length ? (
        <Empty>No income entries in this range.</Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500">
            <tr>
              <th className="border-b px-3 py-2 dark:border-slate-700">Date</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Category</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Source</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Description</th>
              <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Amount</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Attachment</th>
            </tr>
          </thead>
          <tbody>
            {incomesQ.data.incomes.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2 whitespace-nowrap text-slate-500">{i.receivedOn}</td>
                <td className="px-3 py-2">
                  <Badge tone="neutral">
                    {catBySlug.get(i.categorySlug)?.name ?? i.categorySlug}
                  </Badge>
                </td>
                <td className="px-3 py-2">{i.source ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">{i.description ?? '—'}</td>
                <td className="px-3 py-2 text-right">{Money.fromPaise(i.amount).format()}</td>
                <td className="px-3 py-2">
                  {i.attachmentId ? (
                    <a
                      className="text-xs underline"
                      href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/v1/attachments/${i.attachmentId}`}
                      target="_blank" rel="noreferrer"
                    >
                      View
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function IncomeForm({
  categories, onSaved,
}: { categories: Category[]; onSaved: () => void }) {
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [categorySlug, setCategorySlug] = useState(categories[0]?.slug ?? 'other');
  const [source, setSource] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState<'cash' | 'bank' | 'upi' | 'cheque' | 'other' | ''>('');
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/v1/incomes', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const paise = Money.parse(amount).toPaise();
      mutation.mutate({
        amount: paise,
        receivedOn,
        categorySlug,
        source: source || undefined,
        description: description || undefined,
        reference: reference || undefined,
        method: method || undefined,
        attachmentId: attachmentId || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-6 md:items-end">
        <Field label="Amount (₹)">
          <TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="75000" />
        </Field>
        <Field label="Received on">
          <TextInput type="date" required value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
        </Field>
        <Field label="Category">
          <select
            value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            {categories.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
          </select>
        </Field>
        <Field label="Source">
          <TextInput value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Acme Corp" />
        </Field>
        <Field label="Method">
          <select
            value={method} onChange={(e) => setMethod(e.target.value as typeof method)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">—</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
            <option value="upi">UPI</option>
            <option value="cheque">Cheque</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Reference">
          <TextInput value={reference} onChange={(e) => setReference(e.target.value)} placeholder="txn id, cheque no.…" />
        </Field>
        <div className="md:col-span-4">
          <Field label="Description">
            <TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. August salary" />
          </Field>
        </div>
        <div className="md:col-span-2 flex items-end">
          <AttachmentField entityType="income" value={attachmentId} onChange={setAttachmentId} />
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Save income</Button>
        </div>
      </form>
    </Card>
  );
}
